import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import { ShieldCheck, Trash2, Upload, RefreshCw, CheckCircle2, AlertTriangle, ArrowRight, FileText, UploadCloud } from "lucide-react";

const KINDS = [
  { id: "id_front", label: "Photo ID — Front", required: true, hint: "Driver's license, state ID, or passport. Text must be legible." },
  { id: "id_back", label: "Photo ID — Back", required: false, hint: "Optional. Only needed for driver's license / state ID." },
  { id: "proof_of_address", label: "Proof of Address", required: true, hint: "Utility bill, bank statement, lease, insurance statement, or government correspondence. Must be recent and show your name and current address." },
];
const ACCEPT = ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png";

function IdentityPreview({ doc, testid }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let revoke = null;
    let cancelled = false;
    setBlobUrl(null); setErr(null);
    if (!doc?.id) return;
    (async () => {
      try {
        const r = await api.get(`/identity-documents/${doc.id}/preview`, { responseType: "blob" });
        if (cancelled) return;
        const url = URL.createObjectURL(r.data);
        revoke = url;
        setBlobUrl(url);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.status === 404 ? "Preview unavailable" : "Preview failed");
      }
    })();
    return () => { cancelled = true; if (revoke) URL.revokeObjectURL(revoke); };
  }, [doc?.id]);
  if (err) return <div className="p-4 text-xs text-red-600 bg-red-50 rounded-xl border border-red-200" data-testid={`${testid}-error`}>{err}</div>;
  if (!blobUrl) return <div className="p-4 text-xs text-slate-500 bg-slate-50 rounded-xl border border-slate-200" data-testid={`${testid}-loading`}>Loading preview…</div>;
  return doc.mime_type === "application/pdf"
    ? <iframe title={doc.filename} src={blobUrl} className="w-full h-64 bg-white" data-testid={`${testid}-iframe`} />
    : <img alt={doc.filename} src={blobUrl} className="w-full h-64 object-contain bg-white" data-testid={`${testid}-img`} />;
}

export default function IdentityDocuments() {
  const nav = useNavigate();
  const [docs, setDocs] = useState({ id_front: null, id_back: null, proof_of_address: null, ready_for_mailing: false });
  const [busyKind, setBusyKind] = useState(null);
  const [statedAddr, setStatedAddr] = useState("");
  const inputs = useRef({});
  // Read the return-to-mail context once on mount so a mid-page toggle (e.g.,
  // uploading a second doc) doesn't cause the primary action to change on the
  // client. The value is cleared by `/app/documents` after auto-open consumes
  // it; we clear it here only when the user clicks Return to Mail Preview.
  const [returnDocId, setReturnDocId] = useState(null);
  useEffect(() => {
    try { setReturnDocId(localStorage.getItem("iat_return_to_mail_doc") || null); }
    catch { setReturnDocId(null); }
  }, []);
  const goReturnToMail = () => {
    // Documents.jsx has a one-shot useEffect that reads iat_return_to_mail_doc,
    // auto-opens the MailLetterDialog for that id, then removes the key. We
    // don't clear it here so the consumer at /app/documents can complete the
    // hand-off and avoid opening multiple dialogs on refresh.
    nav("/app/documents");
  };
  const goDocuments = () => {
    try { localStorage.removeItem("iat_return_to_mail_doc"); } catch { /* ignore */ }
    nav("/app/documents");
  };
  const goUpload = () => {
    try { localStorage.removeItem("iat_return_to_mail_doc"); } catch { /* ignore */ }
    nav("/app/upload");
  };

  const load = async () => {
    try { const r = await api.get("/identity-documents"); setDocs(r.data); setStatedAddr(r.data.proof_of_address?.stated_address || ""); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load documents"); }
  };
  useEffect(() => { load(); }, []);

  const onFile = async (kind, file) => {
    if (!file) return;
    setBusyKind(kind);
    try {
      const form = new FormData(); form.append("file", file);
      const params = new URLSearchParams({ kind });
      if (kind === "proof_of_address" && statedAddr.trim()) params.set("stated_address", statedAddr.trim());
      await api.post(`/identity-documents/upload?${params.toString()}`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded and metadata stripped");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusyKind(null); if (inputs.current[kind]) inputs.current[kind].value = ""; }
  };

  const del = async (kind) => {
    const d = docs[kind]; if (!d) return;
    if (!window.confirm("Delete this document? You'll need to re-upload before mailing.")) return;
    try { await api.delete(`/identity-documents/${d.id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const permanentDel = async (kind) => {
    const d = docs[kind]; if (!d) return;
    if (!window.confirm("Revoke this document? The stored copy will be overwritten and the record removed. Depending on the storage provider, provider backups may retain a copy for a limited window — see the Production Cutover Checklist.")) return;
    try {
      const r = await api.post(`/identity-documents/${d.id}/permanent-delete`);
      const msg = r?.data?.permanent_deletion_supported ? "Permanently deleted" : "Revoked (best-effort overwrite)";
      toast.success(msg);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Revoke failed"); }
  };

  return (
    <AppShell>
      <div className="max-w-4xl">
        <div className="eyebrow">Identity verification</div>
        <h1 className="text-3xl font-semibold text-slate-900 mt-1">Identity Documents</h1>
        <p className="text-slate-500 text-sm mt-1 max-w-2xl">Bureaus and furnishers require proof of identity and address alongside dispute letters. Files are used only for your dispute mailings.</p>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {KINDS.map((k) => {
            const d = docs[k.id];
            return (
              <Card key={k.id} className="glass rounded-2xl" data-testid={`identity-card-${k.id}`}>
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-slate-900 font-semibold">{k.label}{k.required && <span className="ml-1 text-red-600">*</span>}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{k.hint}</div>
                    </div>
                    {d ? <CheckCircle2 className="text-emerald-600" size={18}/> : k.required ? <AlertTriangle className="text-amber-600" size={18}/> : null}
                  </div>

                  {k.id === "proof_of_address" && (
                    <div>
                      <Label className="text-slate-700 text-xs">Address shown on this document</Label>
                      <Textarea rows={2} placeholder="Line 1, City, State ZIP" value={statedAddr} onChange={(e)=>setStatedAddr(e.target.value)} data-testid="identity-stated-address" className="bg-slate-50 border-slate-200 text-slate-900 text-sm"/>
                      <p className="text-[11px] text-slate-500 mt-1">Type what appears on the document (not what you wish it said). The Mail flow compares this to your return address.</p>
                    </div>
                  )}

                  {d && (
                    <div className="rounded-xl border border-slate-200 overflow-hidden bg-slate-50" data-testid={`identity-preview-${k.id}`}>
                      <IdentityPreview doc={d} testid={`identity-preview-${k.id}`} />
                      <div className="p-2 flex items-center justify-between text-xs text-slate-500 border-t border-slate-200">
                        <span className="truncate max-w-[60%]">{d.filename}</span>
                        <span>{(d.size_bytes/1024).toFixed(1)} KB · {d.mime_type.split("/")[1].toUpperCase()}</span>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2">
                    <input ref={(el)=>inputs.current[k.id]=el} type="file" accept={ACCEPT} onChange={(e)=>onFile(k.id, e.target.files?.[0])} data-testid={`identity-file-${k.id}`} className="hidden"/>
                    <Button onClick={()=>inputs.current[k.id]?.click()} disabled={busyKind === k.id} className="btn-primary rounded-xl" data-testid={`identity-upload-${k.id}`}>
                      {busyKind === k.id ? <><RefreshCw className="animate-spin mr-1" size={14}/>Uploading…</> : <><Upload size={14} className="mr-1"/>{d ? "Replace" : "Upload"}</>}
                    </Button>
                    {d && <Button variant="outline" onClick={()=>del(k.id)} className="rounded-xl border-slate-200 bg-slate-50/70 text-slate-800" data-testid={`identity-delete-${k.id}`}><Trash2 size={14} className="mr-1"/>Delete</Button>}
                    {d && <Button variant="ghost" onClick={()=>permanentDel(k.id)} className="rounded-xl text-red-600 hover:bg-red-50" data-testid={`identity-permdelete-${k.id}`}>Revoke &amp; overwrite</Button>}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className={`mt-6 rounded-2xl border p-5 ${docs.ready_for_mailing ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`} data-testid="identity-mailing-readiness">
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} className={docs.ready_for_mailing ? "text-emerald-600" : "text-amber-600"}/>
            <div className="font-semibold text-slate-900">{docs.ready_for_mailing ? "Ready for mailing" : "Not ready to mail yet"}</div>
          </div>
          <p className="text-sm text-slate-700 mt-2">
            {docs.ready_for_mailing
              ? "Both required documents are uploaded. You can now select Mail this letter on any dispute letter."
              : "Photo ID (front) and Proof of Address are both required before you can mail a dispute letter. You can still draft, edit, and download letters without them."}
          </p>

          {docs.ready_for_mailing && (
            <div className="mt-4 flex flex-col sm:flex-row flex-wrap gap-2" data-testid="identity-completion-actions">
              {returnDocId ? (
                <Button
                  onClick={goReturnToMail}
                  className="btn-primary rounded-xl min-h-[44px] w-full sm:w-auto"
                  data-testid="identity-return-to-mail"
                >
                  <ArrowRight size={16} className="mr-1" />
                  Return to Mail Preview
                </Button>
              ) : (
                <>
                  <Button
                    onClick={goDocuments}
                    className="btn-primary rounded-xl min-h-[44px] w-full sm:w-auto"
                    data-testid="identity-go-documents"
                  >
                    <FileText size={16} className="mr-1" />
                    Go to Document Center
                  </Button>
                  <Button
                    variant="outline"
                    onClick={goUpload}
                    className="rounded-xl border-slate-200 bg-white text-slate-800 min-h-[44px] w-full sm:w-auto"
                    data-testid="identity-upload-report"
                  >
                    <UploadCloud size={16} className="mr-1" />
                    Upload Credit Report
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
